import React from "react";
import {
  Avatar, Box, Button, CircularProgress, Dialog, IconButton,
  OutlinedInput, Stack, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import ApiService from "../../services/api-service";
import notifier from "../Notifier";
import { fullName, getErrorString, getUserId } from "../../constants/function";
import { GIFT_MONTH_OPTIONS, giftTotal } from "./giftTotal";

/**
 * Pay for someone else's subscription to an artist. Mirrors the mobile sheet:
 * pick a member, choose how many months, add a note, pay in SIKI.
 */
export default function GiftSubscription({
  open, onClose, artistId, artistName, monthlyFee, onGifted,
  // The people currently watching, when this is opened from a livestream.
  // Gifting in a stream is about the room you are in, so they come first and
  // searching every member is the fallback.
  candidates = null,
}) {
  const [search, setSearch] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [recipient, setRecipient] = React.useState(null);
  const [months, setMonths] = React.useState(1);
  const [message, setMessage] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const myId = getUserId();

  const total = giftTotal(monthlyFee, months);

  React.useEffect(() => {
    if (!open) {
      setSearch(""); setResults([]); setRecipient(null);
      setMonths(1); setMessage(""); setSending(false);
    }
  }, [open]);

  // Wait for a pause in typing rather than a request per keystroke.
  React.useEffect(() => {
    const keyword = search.trim();
    if (recipient || keyword.length < 2) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    let alive = true;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await ApiService.ADMIN_SEARCH_USER(keyword);
        if (alive) setResults(res?.result || []);
      } catch (e) {
        if (alive) setResults([]);
      } finally {
        if (alive) setSearching(false);
      }
    }, 350);
    return () => { alive = false; clearTimeout(timer); };
  }, [search, recipient]);

  const send = async () => {
    if (!recipient || sending) return;
    setSending(true);
    try {
      await ApiService.GIFT_ARTIST_SUBSCRIPTION({
        artistId,
        recipientId: recipient._id,
        months,
        ...(message.trim() ? { message: message.trim() } : {}),
      });
      notifier.success(
        `Gifted ${fullName(recipient)} ${months} ${months === 1 ? "month" : "months"} of ${artistName}`
      );
      if (onGifted) onGifted();
      onClose();
    } catch (e) {
      notifier.error(getErrorString(e));
    } finally {
      setSending(false);
    }
  };

  const eligible = (u) =>
    u && u._id && String(u._id) !== String(myId) && String(u._id) !== String(artistId);

  const searchHits = results.filter(eligible);

  // An audience row is { user: {...}, uid } in some payloads and the user in
  // others; take whichever carries the id.
  const inStream = (candidates || [])
    .map((c) => (c && c.user ? c.user : c))
    .filter(eligible)
    .filter((u, i, all) => all.findIndex((o) => String(o._id) === String(u._id)) === i);

  const surprise = () => {
    if (inStream.length === 0) return;
    setRecipient(inStream[Math.floor(Math.random() * inStream.length)]);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        style: { backgroundColor: "#141414", borderRadius: 16, width: 420, maxWidth: "100%" },
      }}
    >
      <Stack sx={{ padding: "16px" }} spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <CardGiftcardIcon sx={{ fontSize: 18, color: "#FFEE20" }} />
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>
              Gift {artistName}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.6)" }} />
          </IconButton>
        </Stack>

        {/* Who it is for */}
        {recipient ? (
          <Stack direction="row" alignItems="center" spacing={1.5}
            sx={{ padding: "8px 10px", borderRadius: "10px", background: "#1E1E1E" }}>
            <Avatar src={recipient.profileImg?.filePath || ""} sx={{ width: 30, height: 30 }}>
              {(fullName(recipient) || "?")[0]}
            </Avatar>
            <Typography sx={{ flex: 1, fontSize: 14, color: "#fff" }}>{fullName(recipient)}</Typography>
            <Button size="small" onClick={() => setRecipient(null)} sx={{ color: "#FFEE20", fontSize: 12 }}>
              Change
            </Button>
          </Stack>
        ) : (
          <>
            {/* In a stream, the room comes first: these are the people
                watching right now, the way a gift works on Twitch or Kick. */}
            {inStream.length > 0 ? (
              <>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography sx={{ fontSize: 11, letterSpacing: 1, color: "rgba(255,255,255,0.45)" }}>
                    WATCHING NOW ({inStream.length})
                  </Typography>
                  <Button size="small" onClick={surprise} startIcon={<ShuffleIcon sx={{ fontSize: 14 }} />}
                    sx={{ color: "#FFEE20", fontSize: 12, textTransform: "none" }}>
                    Surprise someone
                  </Button>
                </Stack>
                <Stack spacing={0.75} sx={{ maxHeight: 170, overflowY: "auto" }}>
                  {inStream.map((u) => (
                    <Stack key={String(u._id)} direction="row" alignItems="center" spacing={1.5}
                      onClick={() => setRecipient(u)}
                      sx={{ padding: "8px 10px", borderRadius: "10px", background: "#1E1E1E", cursor: "pointer",
                        "&:hover": { background: "#242424" } }}>
                      <Avatar src={u.profileImg?.filePath || u.profileImg || ""} sx={{ width: 28, height: 28 }}>
                        {(fullName(u) || "?")[0]}
                      </Avatar>
                      <Typography sx={{ fontSize: 14, color: "#fff" }}>{fullName(u)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            ) : null}

            <OutlinedInput
              fullWidth size="small" value={search} autoFocus={inStream.length === 0}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={inStream.length > 0 ? "Or search any member" : "Search members"}
              inputProps={{ "aria-label": "Search members to gift a subscription to" }}
              startAdornment={<SearchIcon sx={{ color: "rgba(255,255,255,0.4)", fontSize: 18, mr: 1 }} />}
              endAdornment={searching ? <CircularProgress size={16} /> : null}
              sx={{ borderRadius: "25px", background: "#1E1E1E", color: "#fff",
                "& fieldset": { borderColor: "rgba(255,255,255,0.12)" } }}
            />
            {search.trim().length >= 2 && !searching ? (
              searchHits.length > 0 ? (
                <Stack spacing={0.75} sx={{ maxHeight: 170, overflowY: "auto" }}>
                  {searchHits.map((u) => (
                    <Stack key={String(u._id)} direction="row" alignItems="center" spacing={1.5}
                      onClick={() => setRecipient(u)}
                      sx={{ padding: "8px 10px", borderRadius: "10px", background: "#1E1E1E", cursor: "pointer",
                        "&:hover": { background: "#242424" } }}>
                      <Avatar src={u.profileImg?.filePath || ""} sx={{ width: 28, height: 28 }}>
                        {(fullName(u) || "?")[0]}
                      </Avatar>
                      <Typography sx={{ fontSize: 14, color: "#fff" }}>{fullName(u)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.35)" }}>
                  No one matches "{search.trim()}".
                </Typography>
              )
            ) : null}
          </>
        )}

        {/* How long */}
        <Stack direction="row" spacing={1}>
          {GIFT_MONTH_OPTIONS.map((m) => (
            <Box key={m} onClick={() => setMonths(m)}
              sx={{ flex: 1, textAlign: "center", padding: "8px 0", borderRadius: "10px", cursor: "pointer",
                background: m === months ? "#FFEE20" : "#1E1E1E",
                border: `1px solid ${m === months ? "#FFEE20" : "rgba(255,255,255,0.12)"}` }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: m === months ? "#141410" : "#fff" }}>{m}</Typography>
              <Typography sx={{ fontSize: 9, letterSpacing: 1, color: m === months ? "#141410" : "rgba(255,255,255,0.5)" }}>
                {m === 1 ? "MONTH" : "MONTHS"}
              </Typography>
            </Box>
          ))}
        </Stack>

        <OutlinedInput
          fullWidth size="small" value={message} onChange={(e) => setMessage(e.target.value)}
          placeholder="Add a note (optional)"
          inputProps={{ "aria-label": "Note to send with the gift", maxLength: 200 }}
          sx={{ borderRadius: "10px", background: "#1E1E1E", color: "#fff",
            "& fieldset": { borderColor: "rgba(255,255,255,0.12)" } }}
        />

        <Stack direction="row" alignItems="baseline" justifyContent="space-between">
          <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>They pay nothing</Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#FFEE20", fontVariantNumeric: "tabular-nums" }}>
            {total} SIKI
          </Typography>
        </Stack>

        <Button
          variant="contained" color="primary" disabled={!recipient || sending || total <= 0}
          onClick={send}
          sx={{ color: "#141410", fontWeight: 700, borderRadius: "10px", textTransform: "none" }}
        >
          {sending ? <CircularProgress size={18} /> : `Gift ${total} SIKI`}
        </Button>
      </Stack>
    </Dialog>
  );
}
